import React, { useId } from 'react';
import {
  ChevronDown, ChevronUp, Plus, RotateCcw, Trash2,
} from 'lucide-react';
import {
  AppButton, Checkbox, FieldError, HelpText, IconButton, Input,
} from '../ui';

// ─── LinkListEditor — an ordered list of website menu links ──────────────
//
// For the header menu, its "More" dropdown and the footer's quick links. A
// row is { label, href, enabled, openInNewTab, icon? }; unticking "Show"
// keeps a link without displaying it, so a seasonal page can come and go
// without being retyped.
//
// Props:
//   title, hint, error
//   links, onChange(links)
//   withIcon  — the "More" menu shows a Bootstrap icon beside each link
//   onReset   — restore the built-in list

export default function LinkListEditor({
  title, hint, error, links, onChange, withIcon = false, onReset,
}) {
  const id = useId();
  const rows = Array.isArray(links) ? links : [];
  const patch = (i, p) => onChange(rows.map((r, idx) => (idx === i ? { ...r, ...p } : r)));
  const move = (i, delta) => {
    const j = i + delta;
    if (j < 0 || j >= rows.length) return;
    const next = [...rows];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };
  const add = () => onChange([...rows, { label: '', href: '', enabled: true, openInNewTab: false, ...(withIcon ? { icon: '' } : {}) }]);

  return (
    <div role="group" aria-labelledby={`${id}-title`} className="tw-flex tw-flex-col tw-gap-2">
      <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2">
        <span id={`${id}-title`} className="tw-text-sm tw-font-medium tw-text-content">{title}</span>
        <div className="tw-flex tw-gap-1">
          {onReset && (
            <AppButton size="sm" variant="ghost" onClick={onReset}>
              <RotateCcw size={16} /> Reset to default
            </AppButton>
          )}
          <AppButton size="sm" variant="outline" onClick={add}>
            <Plus size={16} /> Add link
          </AppButton>
        </div>
      </div>
      {hint && <HelpText>{hint}</HelpText>}

      {rows.length === 0 && (
        <p className="tw-m-0 tw-rounded-sm tw-border tw-border-dashed tw-border-line tw-px-3 tw-py-2.5 tw-text-sm tw-text-content-subtle">
          No links — the built-in list is used until you add one.
        </p>
      )}

      <ul className="tw-m-0 tw-flex tw-list-none tw-flex-col tw-gap-2 tw-p-0">
        {rows.map((link, i) => {
          const name = String(link.label || '').trim() || `link ${i + 1}`;
          return (
            // Index-keyed: links carry no id, and the position is what keeps
            // focus in the row being edited when another moves.
            <li
              key={i}
              className="tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-rounded-sm tw-border tw-border-solid tw-border-line tw-bg-surface-raised tw-p-2"
            >
              <Checkbox
                size="sm"
                label="Show"
                checked={link.enabled !== false}
                onChange={(e) => patch(i, { enabled: e.target.checked })}
              />
              <Input
                size="sm"
                fullWidth={false}
                className="tw-min-w-[120px] tw-flex-1"
                aria-label={`Label for ${name}`}
                placeholder="Label"
                value={link.label || ''}
                onChange={(e) => patch(i, { label: e.target.value })}
              />
              <Input
                size="sm"
                fullWidth={false}
                className="tw-min-w-[160px] tw-flex-[2]"
                aria-label={`Address for ${name}`}
                placeholder="/page or https://…"
                value={link.href || ''}
                onChange={(e) => patch(i, { href: e.target.value })}
              />
              {withIcon && (
                <Input
                  size="sm"
                  fullWidth={false}
                  className="tw-w-[130px]"
                  aria-label={`Icon for ${name}`}
                  placeholder="bi-star"
                  value={link.icon || ''}
                  onChange={(e) => patch(i, { icon: e.target.value })}
                />
              )}
              <Checkbox
                size="sm"
                label="New tab"
                checked={!!link.openInNewTab}
                onChange={(e) => patch(i, { openInNewTab: e.target.checked })}
              />
              <span className="tw-ml-auto tw-flex tw-items-center">
                <IconButton size="xs" icon={ChevronUp} label={`Move ${name} up`} onClick={() => move(i, -1)} disabled={i === 0} />
                <IconButton size="xs" icon={ChevronDown} label={`Move ${name} down`} onClick={() => move(i, 1)} disabled={i === rows.length - 1} />
                <IconButton size="xs" icon={Trash2} variant="ghost-destructive" label={`Remove ${name}`} onClick={() => onChange(rows.filter((_, idx) => idx !== i))} />
              </span>
            </li>
          );
        })}
      </ul>
      {withIcon && (
        <HelpText>
          Icons are Bootstrap Icons names, e.g. bi-star.{' '}
          <a href="https://icons.getbootstrap.com/" target="_blank" rel="noopener noreferrer">Browse icons</a>
        </HelpText>
      )}
      {error && <FieldError>{error}</FieldError>}
    </div>
  );
}
